/**
 * Session Manager (BYK-170, 171, 172, 173)
 *
 * Provides robust session management with:
 * - Proactive token refresh before expiry (BYK-171)
 * - Mutex pattern for concurrent refresh protection (BYK-172)
 * - Graceful error handling - network vs auth errors (BYK-173)
 * - App visibility handling for resume scenarios
 */

// ============================================================================
// Types
// ============================================================================

export interface SessionInfo {
  expiresAt: Date;
  userId: string;
}

export type ErrorType = "auth" | "network" | "unknown";

export interface ClassifiedError {
  type: ErrorType;
  status?: number;
  message: string;
  shouldLogout: boolean;
  shouldRetry: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/** Refresh session 5 minutes before expiry */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** Minimum interval between refresh attempts */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;

/** Maximum retry attempts for network errors */
const MAX_NETWORK_RETRIES = 3;

/** Base delay for exponential backoff (ms) */
const BACKOFF_BASE_MS = 1000;

// ============================================================================
// Error Classification (BYK-173)
// ============================================================================

/**
 * Classify an error to determine appropriate handling
 * Auth errors (401, 403) should trigger logout
 * Network errors (5xx, timeout, offline) should retry
 */
export function classifyError(error: unknown): ClassifiedError {
  // Handle axios errors
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { status: number }; code?: string; message?: string };
    const status = axiosError.response?.status;

    // Auth errors - definitive session issues
    if (status === 401) {
      return {
        type: "auth",
        status,
        message: "Session expired",
        shouldLogout: true,
        shouldRetry: false,
      };
    }

    if (status === 403) {
      return {
        type: "auth",
        status,
        message: "Access forbidden",
        shouldLogout: true,
        shouldRetry: false,
      };
    }

    // Server errors - transient, should retry
    if (status && status >= 500) {
      return {
        type: "network",
        status,
        message: `Server error (${status})`,
        shouldLogout: false,
        shouldRetry: true,
      };
    }

    // Network/timeout errors
    if (axiosError.code === "ECONNABORTED" || axiosError.code === "ETIMEDOUT") {
      return {
        type: "network",
        message: "Request timeout",
        shouldLogout: false,
        shouldRetry: true,
      };
    }

    if (axiosError.code === "ERR_NETWORK") {
      return {
        type: "network",
        message: "Network error - connection lost",
        shouldLogout: false,
        shouldRetry: true,
      };
    }
  }

  // Handle offline state
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return {
      type: "network",
      message: "Device is offline",
      shouldLogout: false,
      shouldRetry: true,
    };
  }

  // Unknown error - don't logout, let user decide
  return {
    type: "unknown",
    message: error instanceof Error ? error.message : "Unknown error",
    shouldLogout: false,
    shouldRetry: false,
  };
}

// ============================================================================
// Retry with Exponential Backoff
// ============================================================================

/**
 * Execute a function with exponential backoff retry
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    baseDelay?: number;
    shouldRetry?: (error: unknown) => boolean;
  } = {}
): Promise<T> {
  const { maxAttempts = MAX_NETWORK_RETRIES, baseDelay = BACKOFF_BASE_MS, shouldRetry } = options;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      const classified = classifyError(error);
      const willRetry = shouldRetry ? shouldRetry(error) : classified.shouldRetry;

      if (!willRetry || attempt === maxAttempts) {
        throw error;
      }

      // Exponential backoff with jitter
      const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 500;
      console.log(`[SessionManager] Retry attempt ${attempt}/${maxAttempts} after ${delay.toFixed(0)}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// ============================================================================
// Mutex Pattern for Concurrent Refresh (BYK-172)
// ============================================================================

/**
 * Creates a mutex-protected function that ensures only one execution at a time.
 * Additional calls while locked will wait for the current execution to complete.
 */
export function createMutex<T>(): {
  acquire: (fn: () => Promise<T>) => Promise<T>;
  isLocked: () => boolean;
} {
  let currentPromise: Promise<T> | null = null;

  return {
    acquire: async (fn: () => Promise<T>): Promise<T> => {
      // If already running, wait for it
      if (currentPromise) {
        console.log("[SessionManager] Waiting for existing refresh operation...");
        return currentPromise;
      }

      // Start new operation
      try {
        currentPromise = fn();
        return await currentPromise;
      } finally {
        currentPromise = null;
      }
    },
    isLocked: () => currentPromise !== null,
  };
}

// ============================================================================
// Session Manager Class
// ============================================================================

export class SessionManager {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRefreshTime: number = 0;
  private refreshMutex = createMutex<boolean>();
  private visibilityHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;

  // Callbacks
  private onSessionRefresh: (() => Promise<SessionInfo | null>) | null = null;
  private onSessionExpired: (() => void) | null = null;
  private onNetworkError: ((message: string) => void) | null = null;

  constructor() {
    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
  }

  /**
   * Initialize the session manager with callbacks
   */
  init(callbacks: {
    onSessionRefresh: () => Promise<SessionInfo | null>;
    onSessionExpired?: () => void;
    onNetworkError?: (message: string) => void;
  }): void {
    this.onSessionRefresh = callbacks.onSessionRefresh;
    this.onSessionExpired = callbacks.onSessionExpired || null;
    this.onNetworkError = callbacks.onNetworkError || null;

    // Set up visibility change handler (BYK-174)
    if (typeof document !== "undefined") {
      this.visibilityHandler = this.handleVisibilityChange;
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }

    // Set up online/offline handlers
    if (typeof window !== "undefined") {
      this.onlineHandler = this.handleOnline;
      window.addEventListener("online", this.onlineHandler);
    }

    console.log("[SessionManager] Initialized");
  }

  /**
   * Start monitoring a session for proactive refresh
   */
  startMonitoring(session: SessionInfo): void {
    this.cancelScheduledRefresh();

    const expiresAt = new Date(session.expiresAt).getTime();
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const timeUntilRefresh = timeUntilExpiry - REFRESH_BUFFER_MS;

    if (timeUntilRefresh <= 0) {
      // Session about to expire or already expired, refresh now
      console.log("[SessionManager] Session near expiry, refreshing now");
      this.doRefresh();
    } else {
      // Schedule refresh before expiry
      console.log(
        `[SessionManager] Scheduling refresh in ${(timeUntilRefresh / 1000 / 60).toFixed(1)} minutes`
      );
      this.refreshTimer = setTimeout(() => this.doRefresh(), timeUntilRefresh);
    }
  }

  /**
   * Perform a session refresh with mutex protection
   */
  async doRefresh(): Promise<boolean> {
    // Rate limit refreshes
    const now = Date.now();
    if (now - this.lastRefreshTime < MIN_REFRESH_INTERVAL_MS) {
      console.log("[SessionManager] Refresh rate limited, skipping");
      return false;
    }

    return this.refreshMutex.acquire(async () => {
      try {
        console.log("[SessionManager] Refreshing session...");
        this.lastRefreshTime = Date.now();

        if (!this.onSessionRefresh) {
          console.error("[SessionManager] No refresh callback configured");
          return false;
        }

        // Use retry logic for network resilience
        const session = await withRetry(
          () => this.onSessionRefresh!(),
          {
            maxAttempts: 2,
            shouldRetry: (error) => {
              const classified = classifyError(error);
              return classified.type === "network";
            },
          }
        );

        if (session) {
          console.log("[SessionManager] Session refreshed successfully");
          this.startMonitoring(session);
          return true;
        } else {
          console.log("[SessionManager] Session refresh returned null");
          this.onSessionExpired?.();
          return false;
        }
      } catch (error) {
        const classified = classifyError(error);
        console.error("[SessionManager] Refresh failed:", classified);

        if (classified.shouldLogout) {
          this.onSessionExpired?.();
        } else if (classified.type === "network") {
          this.onNetworkError?.(classified.message);
          // Schedule retry after network recovery
          this.scheduleNetworkRetry();
        }

        return false;
      }
    });
  }

  /**
   * Handle visibility change (tab focus)
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      console.log("[SessionManager] App resumed, checking session");
      this.doRefresh();
    }
  }

  /**
   * Handle online event (network recovery)
   */
  private handleOnline(): void {
    console.log("[SessionManager] Network restored, checking session");
    this.doRefresh();
  }

  /**
   * Schedule a retry after network error
   */
  private scheduleNetworkRetry(): void {
    // Cancel existing timer
    this.cancelScheduledRefresh();

    // Retry in 30 seconds
    console.log("[SessionManager] Scheduling network retry in 30s");
    this.refreshTimer = setTimeout(() => this.doRefresh(), 30000);
  }

  /**
   * Cancel any scheduled refresh
   */
  private cancelScheduledRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshMutex.isLocked();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.cancelScheduledRefresh();

    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }

    if (this.onlineHandler && typeof window !== "undefined") {
      window.removeEventListener("online", this.onlineHandler);
    }

    console.log("[SessionManager] Destroyed");
  }
}

// Singleton instance
export const sessionManager = new SessionManager();
export default sessionManager;
